import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#262626] text-white focus:border-[#FF4500] focus:outline-none focus:ring-1 focus:ring-[#FF4500] p-4 placeholder-neutral-600 transition-colors duration-200 text-sm";

const initial = { name: "", phone: "", year: "", make: "", model: "", service: "", details: "" };

export const InquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in your name, phone, and desired service.");
      return;
    }
    setSubmitted(true);
    toast.success("Inquiry received! We'll call you back within 24 hours.");
  };

  if (submitted) {
    return (
      <section id="inquiry" data-testid="inquiry-section" className="py-24 md:py-32 bg-[#0F0F0F]">
        <div className="max-w-2xl mx-auto px-6 text-center" data-testid="inquiry-success">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-[#FF4500] text-[#FF4500] mb-8">
            <CheckCircle2 size={36} />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
            Inquiry Received
          </h2>
          <p className="mt-6 text-base md:text-lg text-neutral-400">
            Thanks {form.name.split(" ")[0]} — our team will call you at {form.phone} within 24 hours to talk about your build.
          </p>
          <button
            data-testid="inquiry-reset-btn"
            onClick={() => {
              setForm(initial);
              setSubmitted(false);
            }}
            className="mt-10 bg-transparent text-white font-bold uppercase tracking-wider py-4 px-8 border border-neutral-700 hover:border-[#FF4500] hover:text-[#FF4500] transition-all duration-300"
          >
            Submit Another Inquiry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="inquiry" data-testid="inquiry-section" className="py-24 md:py-32 bg-[#0F0F0F]">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#FF4500]" />
            <span className="text-sm uppercase tracking-[0.3em] text-[#FF4500] font-semibold">Free Quote</span>
            <span className="h-px w-12 bg-[#FF4500]" />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
            Modification Inquiry
          </h2>
          <p className="mt-4 text-base md:text-lg text-neutral-400">
            Tell us about your ride and what you want done. We'll get back to you with a quote.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="inquiry-form" className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="inquiry-name-input" className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} />
            <input data-testid="inquiry-phone-input" className={inputCls} type="tel" placeholder="Phone Number *" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <input data-testid="inquiry-year-input" className={inputCls} placeholder="Vehicle Year" value={form.year} onChange={set("year")} />
            <input data-testid="inquiry-make-input" className={inputCls} placeholder="Make" value={form.make} onChange={set("make")} />
            <input data-testid="inquiry-model-input" className={inputCls} placeholder="Model" value={form.model} onChange={set("model")} />
          </div>
          <select
            data-testid="inquiry-service-select"
            className={`${inputCls} ${form.service ? "text-white" : "text-neutral-600"}`}
            value={form.service}
            onChange={set("service")}
          >
            <option value="" disabled>
              Desired Modification / Service *
            </option>
            <option value="detailing">Premium Car Detailing</option>
            <option value="window-tinting">Window Tinting</option>
            <option value="car-modification">Car Modification (Body Kits, Styling)</option>
            <option value="other">Other / Multiple Services</option>
          </select>
          <textarea
            data-testid="inquiry-details-input"
            className={`${inputCls} min-h-[120px] resize-y`}
            placeholder="Tell us more about what you're looking for (optional)"
            value={form.details}
            onChange={set("details")}
          />
          <button
            type="submit"
            data-testid="inquiry-submit-btn"
            className="group w-full inline-flex items-center justify-center gap-3 bg-[#FF4500] text-white font-bold uppercase tracking-wider py-5 px-8 border border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-all duration-300"
          >
            Inquire Now
            <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>
      </div>
    </section>
  );
};
